import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { PositionType, TooltipPrimitiveProps, TriggerProps } from '@atlaskit/tooltip';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import AutomationIcon from '@atlaskit/icon/core/automation';

import { invokeManuallyTriggeredRule } from '@atlassian/automation-manual-triggers';

import { getAutomationStack } from '@confluence/automation-discovery/entry-points/AutomationAPIUtilities.ts';
import { mapEnv } from '@confluence/automation-menu/entry-points/mapEnv.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { ErrorComponent } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { AUTOMATION_SMART_BUTTON_PREVIEW_RULE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	getMacroExperienceTracker,
	EDITOR,
	RENDERER,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { fg } from '@confluence/feature-gating';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useExperimentalAutomationRuleSummaryQuery } from './queries/useExperimentalAutomationRuleSummaryQuery.ts';
import { useSmartButtonPremiumGate } from './useSmartButtonPremiumGate.ts';
import { useEditRuleData } from './edit/EditSmartButtonRuleContext.tsx';
import type {
	RuleOwnerInfo,
	RuleOwnerInfoVariables,
} from './edit/SmartButtonConfigPanel/__types__/RuleOwnerInfoQuery.ts';
import { getButtonState } from './tooltip/getButtonState.ts';
import { getSmartButtonTooltipContent } from './tooltip/SmartButtonTooltipContent.tsx';
import { isDisabledState, isPreviewState, isEditModeUnconfiguredState } from './buttonStates.ts';
import type { ButtonState } from './buttonStates.ts';
import {
	getFailedObjects,
	onInvocationFailure,
	onInvocationStart,
	onInvocationSuccess,
} from './utils/manualTriggerApiUtils.tsx';
export const i18n = defineMessages({
	defaultSmartButtonName: {
		id: 'automation-extensions.smart-button.default-smart-button-name',
		defaultMessage: 'New button',
		description: 'Default name for a button during creation',
	},
	smartButtonExtensionTitle: {
		id: 'automation-extensions.smart-button.extension-title',
		defaultMessage: 'Smart Button',
		description: 'The title of the smart button extension, mainly used for mobile rendering',
	},
});
const SmartButtonRuleOwnerQuery = graphql`
	query SmartButtonRuleOwnerQuery($userId: ID!) {
		user: confluence_atlassianUser(id: $userId) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

const getTooltipPosition = (buttonState: ButtonState): PositionType => {
	if (isPreviewState(buttonState)) {
		if (fg('a4c-enable-live-doc-smart-button')) {
			return 'right-start';
		}
		return 'bottom-start';
	} else if (isEditModeUnconfiguredState(buttonState)) {
		return 'bottom-end';
	}
	return 'top';
};

const tooltipStyle = css({
	backgroundColor: token('elevation.surface'),
	color: token('color.text'),
	borderRadius: token('radius.small'),
	boxShadow: token('elevation.shadow.overlay'),
	padding: token('space.150'),
	maxWidth: '214px',
});

const TooltipContainer = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(
	function TooltipContainer({ children, ...props }, ref) {
		return (
			<TooltipPrimitive {...props} css={tooltipStyle} ref={ref}>
				{children}
			</TooltipPrimitive>
		);
	},
);

const UnconfiguredIcon = () => (
	<StatusErrorIcon color={token('color.icon.danger')} label="Unconfigured icon" />
);

type SmartButtonProps = {
	flags: FlagsStateContainer;
	smartButtonName?: string;
	ruleId?: string;
	scopeId?: string;
	pageAri?: string;
	site?: string;
	isViewMode: boolean;
	contentType: string;
	experienceTrackerName: string;
	contentId: string;
	predefinedTemplateId?: string;
};

// This component gets rendered in the view and edit mode in editor
export const SmartButtonComponent = withFlags(
	({
		flags,
		ruleId,
		scopeId,
		smartButtonName,
		pageAri,
		site,
		isViewMode,
		contentType,
		experienceTrackerName,
		contentId,
		predefinedTemplateId,
	}: SmartButtonProps) => {
		const [invokingRuleId, setInvokingRuleId] = useState<string | null>(null);
		const [automationStack, setAutomationStack] = useState<string>();
		const experienceTracker = useContext(ExperienceTrackerContext);

		const { environment, cloudId, isAdminHubAIEnabled } = useSessionData();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { error } = useEditRuleData();
		const spaceId = useSpaceId();
		const { isSmartButtonAllowed } = useSmartButtonPremiumGate();

		useEffect(() => {
			if (isSmartButtonAllowed) {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				getAutomationStack(environment).then((res) => setAutomationStack(res));
			}
		}, [environment, isSmartButtonAllowed]);

		useEffect(() => {
			const mode = isViewMode ? RENDERER : EDITOR;
			getMacroExperienceTracker(mode, contentId).succeed({
				name: experienceTrackerName,
			});
		}, [isViewMode, contentId, experienceTrackerName]);

		const mappedEnvironment = mapEnv(environment);

		const smartButtonNameIntl = smartButtonName ?? (
			<FormattedMessage {...i18n.defaultSmartButtonName} />
		);

		// Allow button to be runnable in both view and edit modes if it has all required info
		const isRunnableRule = !!(ruleId && pageAri && site);

		const handleRule = async () => {
			if (isRunnableRule) {
				onInvocationStart(experienceTracker, createAnalyticsEvent);
				setInvokingRuleId(ruleId);
				try {
					// @ts-ignore
					// TODO A4C-3445: Remove this ignore once rule uuid strings are supported
					const response = await invokeManuallyTriggeredRule(mappedEnvironment, site, ruleId, [
						pageAri,
					]);
					const failedObjects: string[] = getFailedObjects(response);
					setInvokingRuleId(null);
					if (failedObjects.length === 0) {
						onInvocationSuccess(flags, experienceTracker);
					} else {
						onInvocationFailure(
							flags,
							experienceTracker,
							new Error('Failed to run automation rule'),
							{ failedObjects: JSON.stringify(failedObjects) },
						);
					}
				} catch (e: any) {
					onInvocationFailure(flags, experienceTracker, e);
					setInvokingRuleId(null);
				}
			}
		};

		const {
			data: automationRuleSummaryData,
			loading: automationRuleSummaryLoading,
			error: automationRuleSummaryError,
		} = useExperimentalAutomationRuleSummaryQuery({
			skip: !ruleId || !automationStack || !spaceId,
			variables: {
				ruleId: ruleId || '',
				cloudId,
				automationStack: automationStack || '',
				spaceId: spaceId || '',
			},
		});

		if (automationRuleSummaryError) {
			// 404s represent a deleted rule and not a failed experience
			if (!isStatusCodeError(automationRuleSummaryError, '404')) {
				experienceTracker.fail({
					name: AUTOMATION_SMART_BUTTON_PREVIEW_RULE,
					error: automationRuleSummaryError,
				});
			}
			markErrorAsHandled(automationRuleSummaryError);
		}

		const accountId = automationRuleSummaryData?.experimentalAutomationRuleSummary?.authorAccountId;
		const { data } = useQueryInterop<RuleOwnerInfo, RuleOwnerInfoVariables>(
			SmartButtonRuleOwnerQuery,
			{
				skip: !accountId,
				variables: { userId: accountId || '' },
			},
		);
		const userName = data?.user?.displayName;

		const isCurrentPageLive = useIsCurrentPageLive();

		const buttonState = getButtonState({
			automationRuleSummaryData,
			automationRuleSummaryLoading,
			contentType,
			isAdminHubAIEnabled,
			isSmartButtonAllowed,
			isViewMode,
			ruleId,
			scopeId,
			spaceId,
			isRunnableRule,
			predefinedTemplateId,
			isCurrentPageLive,
			error,
		});

		const tooltipContent = getSmartButtonTooltipContent({
			buttonState,
			userName,
			automationRuleSummaryData,
			automationRuleSummaryLoading,
			automationRuleSummaryError,
		});

		const isButtonLoading = ruleId === invokingRuleId;
		const isButtonDisabled = isDisabledState(buttonState);

		const shouldUseCustomTooltipStyle = isPreviewState(buttonState);
		const shouldShowUnconfiguredIcon = isEditModeUnconfiguredState(buttonState);
		const tooltipPosition = getTooltipPosition(buttonState);

		return (
			<Tooltip
				delay={0}
				position={tooltipPosition}
				content={tooltipContent}
				component={
					shouldUseCustomTooltipStyle
						? (TooltipContainer as React.ComponentType<TooltipPrimitiveProps>)
						: undefined
				}
			>
				{(tooltipProps: TriggerProps) => {
					const { ...restTooltipProps } = tooltipProps;
					return (
						<Button
							{...restTooltipProps}
							onClick={handleRule}
							isDisabled={isButtonDisabled}
							iconBefore={AutomationIcon}
							iconAfter={shouldShowUnconfiguredIcon ? UnconfiguredIcon : undefined}
							isLoading={isButtonLoading}
						>
							{smartButtonNameIntl}
						</Button>
					);
				}}
			</Tooltip>
		);
	},
);

export const SmartButton = (props: Omit<SmartButtonProps, 'flags'>) => {
	const { isViewMode, contentId, experienceTrackerName, ruleId } = props;
	return (
		<GenericErrorBoundary
			renderOnError={(errorProps) => (
				<>
					<ErrorComponent {...errorProps} />
					<MacroExperienceFailure
						name={experienceTrackerName}
						contentId={contentId}
						mode={isViewMode ? RENDERER : EDITOR}
						error={errorProps.error}
						attributes={{
							ruleId,
						}}
					/>
				</>
			)}
			attribution={Attribution.AUTOMATION_FOR_CONFLUENCE}
		>
			<SmartButtonComponent {...props} />
			<ExperienceStart name={AUTOMATION_SMART_BUTTON_PREVIEW_RULE} />
		</GenericErrorBoundary>
	);
};
